import { Link } from 'react-router-dom';
import { LEGAL } from '@affily/shared/constants';
import { Seo } from '../components/Seo.jsx';
import { LegalPage, LegalSection, LegalList, ContactLink } from '../components/Legal.jsx';

/**
 * Terms of Service.
 *
 * DRAFT. Written from what the code does (tracking, attribution, settle-then-pay
 * flow, frozen partnership terms, draft-only API profiles) and not reviewed by a
 * lawyer. The entity name, contact address and governing law live in
 * LEGAL (shared/src/constants.js) and are unconfirmed.
 */
export default function Terms() {

  return (
    <>
      <Seo
        title="Terms of Service"
        description="The rules for using Affily: accounts, programs and frozen partnership terms, the platform fee, how commissions are settled and paid, and what we expect from creators and brands."
      />

      <LegalPage
        title="Terms of Service"
        intro="These are the rules for using Affily. We have tried to write them in plain language, because terms nobody can read protect nobody."
        summary={[
          'Affily is a marketplace where brands publish partnership programs and creators find and join them. A public program’s terms are open to everyone; a private program’s are shared with the creators its brand invites or accepts.',
          `When a creator joins a program, that program’s terms are frozen for that partnership. The brand cannot change them afterwards.`,
          'Brands owe the commissions they have agreed to. Creators are paid through Stripe. Our fee is split between the brand and the creator, and each sees their share in their account before anything is paid.',
          'Sales we cannot trace back to a creator’s tracked link are not paid.',
          'Creators must not cheat (fake clicks, clicking their own links) and must disclose that they earn from links.',
          'Nothing a key or an AI agent creates for you is public until you sign in and publish it yourself.',
        ]}
      >
        <LegalSection title="1. Who we are, and what these terms cover">
          <p>
            Affily is operated by {LEGAL.entityName} (“we”, “us”). These terms are the agreement
            between you and us when you use affily.me, its tools and its API (together, “Affily”).
            By creating an account, using a tracked link, or using the site you agree to them. If
            you do not agree, do not use Affily.
          </p>
          <p>
            Our <Link to="/privacy" className="text-accent underline underline-offset-2">Privacy Policy</Link>{' '}
            explains what personal data we handle, and{' '}
            <Link to="/transparency" className="text-accent underline underline-offset-2">
              How transparency works
            </Link>{' '}
            explains how the public information on Affily is labelled.
          </p>
        </LegalSection>

        <LegalSection title="2. Who can use Affily">
          <p>
            You must be at least 18 and able to enter a binding agreement. If you sign up on behalf
            of a company, you confirm you have authority to bind it, and “you” means the company
            too. You must not use Affily if we have previously closed your account for breaking
            these terms.
          </p>
        </LegalSection>

        <LegalSection title="3. Your account">
          <LegalList>
            <li>
              You can sign up with an email address and password, or with Google sign-in where it
              is enabled. One account can hold both a creator role and a brand role.
            </li>
            <li>
              Give us accurate information and keep it current. Keep your password and any API
              keys secret. You are responsible for what happens under your account, including
              through a key you have given to a person or an AI agent.
            </li>
            <li>Tell us promptly at <ContactLink /> if you think your account has been compromised.</li>
          </LegalList>
        </LegalSection>

        <LegalSection title="4. Profiles, API keys and AI agents">
          <p>
            You can build a profile yourself or let a person or software agent do it for you using
            an API key. We treat anything created with an API key as a draft. A draft is not in the
            directory, not in the sitemap, and its public page does not exist until the account
            holder signs in and publishes it. Publishing, changing a password, creating keys and
            marking anything as verified all require a signed-in human.
          </p>
          <p>
            If you hand a key to an agent, you are responsible for what it writes on your behalf,
            and for reviewing the draft before you publish. Once you publish, the profile is yours.
          </p>
        </LegalSection>

        <LegalSection title="5. Programs and partnership terms">
          <p>
            A brand publishes a program with its commission type and rate, cookie window, payout
            schedule, any recurring period, and content requirements. These are public to everyone.
            A creator can apply; the brand can accept or decline.
          </p>
          <p>
            When a partnership is accepted, the program’s terms at that moment are copied into the
            partnership and frozen. Neither side can edit them afterwards, and a later change to
            the program’s public terms does not affect partnerships already accepted. The frozen
            terms, not the live program page, decide what is owed on that partnership.
          </p>
          <p>
            The partnership is an agreement between the creator and the brand. We provide the
            marketplace, link tracking, record-keeping and the payment mechanism, but we are not a
            party to what a brand and a creator agree, and we are not their employer, agent or
            representative.
          </p>
        </LegalSection>

        <LegalSection title="6. What brands agree to">
          <LegalList>
            <li>
              To pay the commissions earned under the frozen terms of each partnership, in full,
              including recurring commissions for the period the terms promise.
            </li>
            <li>
              To keep a working payment method on file (see section 9) so a payout can be charged
              when you start one.
            </li>
            <li>
              To report conversions honestly and completely: through a connected Stripe or Shopify
              account, signed server-to-server callbacks, or the browser tag. Do not suppress,
              under-report or misattribute sales.
            </li>
            <li>
              To reverse a commission only for a real reason such as a refund, a cancelled order or
              fraud, and to give that reason. A commission that has already been paid out cannot be
              reversed through Affily.
            </li>
            <li>
              To keep the program’s content requirements accurate, and not to ask creators to make
              false or unlawful claims about your product.
            </li>
            <li>
              To keep your integration’s signing secret private. It is the only thing that stops
              other people from forging conversions against your program.
            </li>
          </LegalList>
        </LegalSection>

        <LegalSection title="7. What creators agree to">
          <LegalList>
            <li>
              No fraud. That includes clicking your own links, paying or incentivising fake clicks
              or purchases, using bots or click farms, cookie stuffing, and any other way of making
              a sale look like it came from you when it did not.
            </li>
            <li>
              No misleading claims about the brand, its products or your own results, and nothing
              the program’s content requirements prohibit.
            </li>
            <li>
              Disclose the relationship. If you earn money from a link or product you promote, say
              so clearly and prominently in the content itself, as the law where you and your
              audience are requires. That includes the US Federal Trade Commission’s endorsement
              rules and the UK Advertising Standards Authority and CMA rules. Disclosure is your
              responsibility, not ours and not the brand’s.
            </li>
            <li>
              Audience statistics (followers, average views, engagement, demographics) are entered
              by you and shown publicly, labelled as self-reported. Enter only what you believe is
              true. Do not inflate numbers.
            </li>
          </LegalList>
        </LegalSection>

        <LegalSection title="8. Tracking and attribution">
          <p>
            Tracked links redirect through affily.me, record the click, and pass a click identifier
            to the brand’s site. A conversion is credited to a creator only if it can be matched to
            one of their tracked clicks, using the click identifier the brand reports back, or,
            as a fallback, a match on the same program from the same browser and network, inside the
            partnership’s frozen cookie window. Renewals of a subscription are credited by the
            subscription’s earlier attributed sale, for as long as the recurring terms say.
          </p>
          <p>
            If a brand turns on creator codes, each creator on that program gets their own discount
            code. A conversion that reports an active creator’s code is credited to that creator,
            with no click and no cookie window required, and the code takes precedence over any
            click: the shopper named the creator themselves. A code stops earning when its
            partnership is paused or ended, or when the brand turns codes off. An action the brand
            pays once per referred visitor is never paid by code, because a code cannot tell one
            visitor from another.
          </p>
          <p>
            <strong className="text-ink">A conversion we cannot attribute to a creator is recorded
            and shown to the brand but is not paid.</strong> Repeat clicks by the same visitor inside
            24 hours count as one unique click for statistics. Our counts are the records both sides
            rely on, but they are measured from our systems and can be affected by things outside
            our control, such as ad blockers, browser privacy features and a brand’s own integration.
            We do not guarantee that every sale will be tracked.
          </p>
        </LegalSection>

        <LegalSection title="9. Fees, settling and getting paid">
          <p>
            It is free to join, as a creator or a brand. Our only charge is a platform fee on each
            commission, set by what the program pays for and shared between both sides: half comes
            out of the commission the creator earns, and half is added to what the brand pays. Each
            side’s rate is shown on the pricing page in its account, and on every commission, before
            anything is paid. Brands paying by card also pay the card fee shown before they pay;
            paying by bank transfer adds none.
          </p>
          <p>The flow, in order:</p>
          <LegalList>
            <li>A tracked conversion creates a commission, shown to both sides with its fee and net amount.</li>
            <li>
              The brand reviews its commissions, may reverse one for a real reason (section 6), and
              then <strong className="text-ink">settles</strong> the ones it confirms. Settling is
              the brand saying the order is genuine and the commission is payable.
            </li>
            <li>
              The brand then starts a payout for a creator. At that point the brand’s saved payment
              method (or money it has sent us by bank transfer) is charged the commission plus its
              share of our fee, and the creator receives the <strong className="text-ink">net</strong>{' '}
              amount, the commission less their share, by transfer to the creator’s connected Stripe
              account.
            </li>
          </LegalList>
          <p>
            Payments are processed by Stripe, and you also agree to Stripe’s terms. Creators must
            complete Stripe’s onboarding (identity and bank details are collected by Stripe, not by
            us) before they can receive a payout. Brands save a card with Stripe; we charge it
            without the brand present when a payout is started, so a card that needs extra
            authentication, or that is declined, will make the payout fail. Brands are charged when a
            payout is started, not before. The program’s stated payout schedule
            is the brand’s commitment to creators about how quickly it settles; we do not pay a
            commission that a brand has not settled, and we do not guarantee a brand’s solvency or
            willingness to pay.
          </p>
          <p>
            Amounts are in the currency recorded on the conversion, US dollars by default. You are
            responsible for your own taxes. We may change the platform fee for the future; a change
            will not apply to commissions already accrued. Affily Boost, the paid featured
            placement shown on the site, is not available to buy yet. If it launches it will have
            its own terms.
          </p>
        </LegalSection>

        <LegalSection title="10. Reviews">
          <p>
            After a partnership is completed, each side can leave one public review of the other.
            Reviews must be honest, based on the partnership, and free of abuse, personal data and
            anything unlawful. A review is public to both sides on the same terms, and the person
            reviewed cannot hide it. We may hide a review that breaks these terms.
          </p>
        </LegalSection>

        <LegalSection title="11. Prohibited conduct">
          <p>Do not:</p>
          <LegalList>
            <li>break the law, or use Affily to promote anything illegal or unsafe;</li>
            <li>commit or assist fraud, including forged or inflated conversions, and fake accounts or reviews;</li>
            <li>mark self-reported numbers as verified, or misrepresent who you are or who you work for;</li>
            <li>upload anything you do not have the right to use, or that is unlawful, infringing, or harmful;</li>
            <li>scrape private data, probe for weaknesses, overload the service, or try to get around rate limits or access controls;</li>
            <li>use the API for anything other than the scopes the key was granted for, or share a key with people you would not trust with the account;</li>
            <li>use Affily’s public data to build a competing directory without asking us first.</li>
          </LegalList>
          <p>
            Reading the public directory and the public API endpoints needs no account, and you are
            welcome to do so for ordinary use.
          </p>
        </LegalSection>

        <LegalSection title="12. Your content, and what we may do with it">
          <p>
            You own what you upload or write (profile text, images, video, links, program
            descriptions, reviews). By putting it on Affily you give us a worldwide, non-exclusive,
            royalty-free licence to host, store, display, reproduce and distribute it, in order to
            run Affily. That includes showing your published profile, programs and reviews publicly
            and to search engines, and creating thumbnails or resized copies. The licence ends when
            you remove the content or your account is closed, except for copies that are
            reasonably needed to keep records (for example, a payout record), or that are still in
            other people’s caches or backups for a short time.
          </p>
          <p>
            You confirm that you have the rights to give this licence and that your content is
            accurate. If you think something on Affily infringes your rights, email{' '}
            <ContactLink /> with enough detail for us to find it.
          </p>
        </LegalSection>

        <LegalSection title="13. Ending your account">
          <p>
            You can stop using Affily at any time and can ask us to close your account by emailing{' '}
            <ContactLink />. Closing an account does not cancel what you owe, or are owed:
            commissions already earned under frozen partnership terms stay payable, and
            partnerships should be wound down properly with the other side.
          </p>
          <p>
            We may suspend or close an account, remove content, or withhold a payout if we
            reasonably believe you have broken these terms, committed fraud, or put other users or
            Affily at risk, or if the law requires. Where we can, we will tell you why. Sections that
            by their nature should survive (payment obligations, licences needed to keep records,
            disclaimers, limitation of liability, governing law) do.
          </p>
        </LegalSection>

        <LegalSection title="14. Disclaimers">
          <p>
            Affily is provided “as is” and “as available”. We work hard to keep it accurate and
            running, but we do not promise that it will be uninterrupted or error-free, that
            tracking will capture every sale, or that you will earn or save any particular amount.
          </p>
          <p>
            Self-reported statistics are the user’s own claims and we have not checked them.
            Program terms, creator rates and reviews are written by other users. We are not
            responsible for what they say or for the results of any deal between a brand and a
            creator. Nothing here is legal, tax or financial advice, and the earnings and cost
            calculators are illustrations, not forecasts.
          </p>
        </LegalSection>

        <LegalSection title="15. Limits on our liability">
          <p>
            Nothing in these terms limits liability that the law does not allow to be limited,
            such as liability for fraud, or for death or personal injury caused by negligence.
          </p>
          <p>
            Subject to that, we are not liable for indirect or consequential loss, lost profits,
            lost sales, lost data or loss of goodwill, or for loss caused by something outside our
            reasonable control (for example, an outage at Stripe, your host or your email
            provider). Our total liability to you for any claim connected to Affily is limited to
            the platform fees you paid us in the 12 months before the claim arose, or, if you have
            paid none, to 100 US dollars.
          </p>
          <p>
            You agree to cover our reasonable losses if your content, your conduct or your breach of
            these terms results in a claim against us from a third party.
          </p>
        </LegalSection>

        <LegalSection title="16. Changes to these terms">
          <p>
            We may update these terms. The “last updated” date at the top shows when. For a
            material change we will email the address on your account, or show a notice on the
            site, before it takes effect where we reasonably can. If you keep using Affily after a
            change takes effect, you accept it. A change never alters the frozen terms of a
            partnership that has already been accepted.
          </p>
        </LegalSection>

        <LegalSection title="17. Governing law">
          {LEGAL.governingLaw ? (
            <p>These terms are governed by {LEGAL.governingLaw}.</p>
          ) : (
            <p>
              <strong className="text-ink">[Governing law and courts: not yet set.]</strong> The
              owner must state the governing law and where disputes are heard before launch. Until
              then this section is incomplete.
            </p>
          )}
          <p>
            If you are a consumer, you also keep any rights you have under the mandatory consumer
            protection law of the place where you live.
          </p>
        </LegalSection>

        <LegalSection title="18. Contact">
          <p>
            Questions, notices or complaints: <ContactLink />. {LEGAL.entityName}.
          </p>
        </LegalSection>
      </LegalPage>
    </>
  );
}
